import { makeStyles, tokens } from "@fluentui/react-components";
import { $cloud10, $sky60, $softInfoBackground } from "../../../cssjs/colors";

const flexSpaceBetween = {
  display: "flex",
  justifyContent: "space-between",
} as const;

const flexSpaceBetweenCenter = {
  ...flexSpaceBetween,
  alignItems: "center",
} as const;

const flexSpaceBetweenCenterWrap = {
  ...flexSpaceBetweenCenter,
  flexWrap: "wrap",
} as const;

const flexWrapCenter = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
} as const;

export const usePanelStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    maxWidth: "560px",
    boxSizing: "border-box",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    overflowX: "hidden",
  },
});

export const useHeaderStyles = makeStyles({
  header: {
    ...flexSpaceBetweenCenterWrap,
    rowGap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: "anywhere",
  },
  logsLink: {
    color: $sky60,
    fontWeight: tokens.fontWeightSemibold,
    cursor: "pointer",
    textDecoration: "underline",
  },
});

export const useContentStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
  },
  divider: {
    flexGrow: 0,
    flexShrink: 0,
  },
  listHeader: {
    ...flexSpaceBetweenCenterWrap,
    justifyContent: "flex-end",
    rowGap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },
  listActions: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXS,
  },
  actionLabel: {
    color: $sky60,
    fontWeight: tokens.fontWeightSemibold,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    rowGap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  },
  listPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "120px",
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    overflowWrap: "anywhere",
  },
});

export const useFiltersStyles = makeStyles({
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    rowGap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  filterLabel: {
    paddingBottom: tokens.spacingVerticalXS,
  },
  dropdown: {
    minWidth: "80px",
    color: $sky60,
    borderTopColor: $sky60,
    borderRightColor: $sky60,
    borderBottomColor: $sky60,
    borderLeftColor: $sky60,
    "& button": {
      color: $sky60,
      padding: `${tokens.spacingHorizontalXS} ${tokens.spacingHorizontalXS} ${tokens.spacingHorizontalXS} ${tokens.spacingHorizontalS}`,
    },
    "& .fui-Dropdown__expandIcon": {
      color: $sky60,
    },
  },
  severityDropdown: {
    maxWidth: "90px",
  },
  categoryDropdown: {
    maxWidth: "112px",
  },
  timeDropdown: {
    maxWidth: "96px",
  },
  reset: {
    marginLeft: "auto",
    alignSelf: "flex-end",
    color: $sky60,
    fontWeight: tokens.fontWeightSemibold,
    cursor: "pointer",
    padding: 0,
    minWidth: 0,
  },
});

export const useCardStyles = makeStyles({
  card: {
    display: "flex",
    columnGap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: $cloud10,
    boxShadow: "0px 0px 2px 0px #104EA466",
  },
  cardCheckbox: {
    flexShrink: 0,
    "& div": {
      margin: `${tokens.spacingHorizontalS} ${tokens.spacingHorizontalXS}`,
    },
  },
  cardMain: {
    display: "flex",
    flexDirection: "column",
    rowGap: tokens.spacingVerticalS,
    minWidth: 0,
    flexGrow: 1,
  },
  cardTitleRow: {
    ...flexSpaceBetweenCenter,
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: "anywhere",
    minWidth: 0,
  },
  cardTitleWithIcon: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  cardTitleIconContainer: {
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    padding: "6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
    backgroundColor: $softInfoBackground,
  },
  cardTitleIcon: {
    width: "100%",
    height: "100%",
    flexShrink: 0,
  },
  cardTimestamp: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
  },
  cardTagsContainer: {
    ...flexSpaceBetweenCenter,
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS,
  },
  cardTags: {
    ...flexWrapCenter,
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS,
  },
  cardImpact: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflowWrap: "anywhere",
    flexShrink: 0,
  },
  cardImpactLabel: {
    fontWeight: tokens.fontWeightBold,
  },
  cardBody: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflowWrap: "anywhere",
  },
});
